/**
 * DYN-3 Batch E — Geometric Constraint pick helpers.
 *
 * Extracted from draftModifyStateMachine.ts to keep that file under
 * the 2000-line CLAUDE.md budget. The pick flow is entered from the
 * BlockAuthoringPalette (onStartGeometricConstraint); subsequent
 * clicks route through `advanceGeomConstraintPick` until the required
 * arity is met, at which point a `geometry/add-geometric-constraint`
 * command is dispatched.
 *
 * ## Public API
 *
 *   - advanceGeomConstraintPick(session, entityId)
 *       → add an entity id to the session and decide the next step
 *   - commitGeomConstraintFromMore(session)
 *       → commit an unbounded-arity constraint on Enter
 *
 * ## Session options schema (all under DraftSession.options)
 *
 *   - `geomKind` — the constraint kind string (from geometricConstraintKindSchema)
 *   - `geomBlockName` — target BLOCK name (BEDIT context)
 *   - `geomPickedIds` — JSON-encoded string[] of entity ids picked so far
 *
 * The caller primes these on startDraftSession("GCCONSTRAINT", …).
 */

import { t } from "../i18n";
import type {
  DraftCommitResult,
  DraftSession,
} from "./draftTypes";

/* ── Arity table ────────────────────────────────────── */

/**
 * Per-kind pick arity for geometric constraints. Mirrors ARITY_MIN /
 * ARITY_MAX in dynamicBlockConstraints.ts; kept here as a plain data
 * table so the shell flow doesn't import server-side validation.
 *
 * `multiple=true` means the kind accepts unbounded entities (≥2):
 * after the second pick, extra picks keep adding to the list; user
 * commits via Enter (empty text) → commitGeomConstraintFromMore.
 *
 * `needsReferenceLine=true` means the kind requires one extra pick
 * after the last entity pick (currently only "symmetric").
 */
export const GEOM_CONSTRAINT_ARITY: Record<
  string,
  { required: number; multiple: boolean; needsReferenceLine: boolean }
> = {
  coincident: { required: 2, multiple: true, needsReferenceLine: false },
  collinear: { required: 2, multiple: true, needsReferenceLine: false },
  concentric: { required: 2, multiple: true, needsReferenceLine: false },
  fix: { required: 1, multiple: false, needsReferenceLine: false },
  parallel: { required: 2, multiple: false, needsReferenceLine: false },
  perpendicular: { required: 2, multiple: false, needsReferenceLine: false },
  tangent: { required: 2, multiple: false, needsReferenceLine: false },
  equal: { required: 2, multiple: false, needsReferenceLine: false },
  symmetric: { required: 2, multiple: false, needsReferenceLine: true },
  smooth: { required: 2, multiple: false, needsReferenceLine: false },
  horizontal: { required: 1, multiple: false, needsReferenceLine: false },
  vertical: { required: 1, multiple: false, needsReferenceLine: false },
};

/* ── Picked-ids serialization ────────────────────────── */

export function parseGeomPickedIds(raw: string | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((id): id is string => typeof id === "string");
    }
  } catch {
    // fallthrough
  }
  return [];
}

export function encodeGeomPickedIds(ids: readonly string[]): string {
  return JSON.stringify(ids);
}

/* ── Advance ─────────────────────────────────────────── */

/**
 * Advance the geometric-constraint pick session by one entity id. The
 * caller (draftModify switch on step) has already hit-tested the click
 * and produced the entity id; this helper decides the next step (or
 * commit).
 *
 * Returns:
 *   - nextSession only (stay in draft): still collecting picks
 *   - commit + nextSession=null: arity satisfied, dispatch
 *     `geometry/add-geometric-constraint`.
 */
export function advanceGeomConstraintPick(
  session: DraftSession,
  entityId: string,
): { nextSession: DraftSession | null; commit?: DraftCommitResult } {
  const kind = session.options.geomKind;
  const blockName = session.options.geomBlockName;
  if (!kind || !blockName) return { nextSession: session };
  const arity = GEOM_CONSTRAINT_ARITY[kind];
  if (!arity) return { nextSession: session };

  const picked = parseGeomPickedIds(session.options.geomPickedIds);
  // De-dupe consecutive picks so a double-click doesn't queue the
  // same entity twice.
  if (picked[picked.length - 1] === entityId) {
    return { nextSession: session };
  }

  // Reference-line step: always the FINAL pick for the kinds that need
  // it ("symmetric"). We get here after `required` entities are picked.
  if (session.step === "geom-constraint-pick-reference-line") {
    const id = `gc-${kind}-${Date.now().toString(36)}`;
    const constraint = {
      id,
      kind,
      entityIds: [...picked],
      referenceLineId: entityId,
    };
    return {
      nextSession: null,
      commit: {
        geometryCommand: {
          type: "geometry/add-geometric-constraint",
          source: "scene",
          blockName,
          constraint,
        },
        summary: t("summary.geom_constraint_added", { kind }),
        commandText: `GC${kind.toUpperCase()}`,
      },
    };
  }

  // Otherwise: append the picked id to the collection.
  const nextPicked = [...picked, entityId];
  const nextOpts = {
    ...session.options,
    geomPickedIds: encodeGeomPickedIds(nextPicked),
  };

  // Check whether we've reached the required entity arity.
  if (nextPicked.length < arity.required) {
    // Still gathering mandatory picks: transition into second step.
    return {
      nextSession: {
        ...session,
        step: "geom-constraint-pick-second",
        options: nextOpts,
        hoverPoint: null,
      },
    };
  }

  // Required arity reached. Route to reference-line pick or pick-more
  // for unbounded kinds, or commit immediately for fixed-arity kinds.
  if (arity.needsReferenceLine) {
    return {
      nextSession: {
        ...session,
        step: "geom-constraint-pick-reference-line",
        options: nextOpts,
        hoverPoint: null,
      },
    };
  }
  if (arity.multiple) {
    return {
      nextSession: {
        ...session,
        step: "geom-constraint-pick-more",
        options: nextOpts,
        hoverPoint: null,
      },
    };
  }

  // Fixed-arity kinds commit immediately at the required arity.
  const id = `gc-${kind}-${Date.now().toString(36)}`;
  const constraint = { id, kind, entityIds: nextPicked };
  return {
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/add-geometric-constraint",
        source: "scene",
        blockName,
        constraint,
      },
      summary: t("summary.geom_constraint_added", { kind }),
      commandText: `GC${kind.toUpperCase()}`,
    },
  };
}

/**
 * Handle "Enter" during geom-constraint-pick-more: commits the
 * unbounded-arity constraint with whatever has been picked so far
 * (at least the required minimum).
 */
export function commitGeomConstraintFromMore(session: DraftSession): {
  nextSession: DraftSession | null;
  commit?: DraftCommitResult;
} {
  const kind = session.options.geomKind;
  const blockName = session.options.geomBlockName;
  if (!kind || !blockName) return { nextSession: session };
  const arity = GEOM_CONSTRAINT_ARITY[kind];
  if (!arity) return { nextSession: session };
  const picked = parseGeomPickedIds(session.options.geomPickedIds);
  if (picked.length < arity.required) return { nextSession: session };
  const id = `gc-${kind}-${Date.now().toString(36)}`;
  const constraint = { id, kind, entityIds: [...picked] };
  return {
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/add-geometric-constraint",
        source: "scene",
        blockName,
        constraint,
      },
      summary: t("summary.geom_constraint_added", { kind }),
      commandText: `GC${kind.toUpperCase()}`,
    },
  };
}
